import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTutorial } from '@/components/tutorial/use-tutorial';
import TutorialTooltip, { type TargetRect } from '@/components/tutorial/tutorial-tooltip';

const SPOTLIGHT_PADDING = 12;

// Header is h-16 (64px), BottomNav is h-16 (64px)
const HEADER_HEIGHT = 64;
const BOTTOM_NAV_HEIGHT = 64;

const TutorialOverlay = () => {
  const { currentStepData, waitingForInteraction, getTargetSelector, skipTutorial } = useTutorial();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const rafRef = useRef<number | null>(null);

  const updateTargetRect = useCallback(() => {
    if (!currentStepData) {
      setTargetRect(null);
      return;
    }

    const selector = getTargetSelector?.(currentStepData);
    if (!selector) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(selector);
    if (!el) {
      setTargetRect(null);
      return;
    }

    const rect = el.getBoundingClientRect();

    // Skip hidden elements (display:none, hidden by CSS, or zero size)
    if (rect.width === 0 && rect.height === 0) {
      setTargetRect(null);
      return;
    }
    const isMobile = window.innerWidth < 1024;

    // Check if element is in the visible area (accounting for sticky header + bottom nav)
    const topBound = HEADER_HEIGHT;
    const bottomBound = isMobile ? window.innerHeight - BOTTOM_NAV_HEIGHT : window.innerHeight;
    const isInView = rect.top >= topBound - 20 && rect.bottom <= bottomBound + 20;

    if (!isInView) {
      // Scroll with offset for sticky header
      const elementTop = rect.top + window.scrollY;
      const scrollTo = elementTop - HEADER_HEIGHT - 40;
      const start = window.scrollY;
      const end = Math.max(0, scrollTo);
      const distance = end - start;
      const duration = Math.min(200, Math.abs(distance) * 0.3);
      const startTime = performance.now();

      const animateScroll = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        window.scrollTo(0, start + distance * ease);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          const newRect = el.getBoundingClientRect();
          setTargetRect({
            top: newRect.top,
            left: newRect.left,
            width: newRect.width,
            height: newRect.height,
            bottom: newRect.bottom,
            right: newRect.right,
          });
        }
      };
      requestAnimationFrame(animateScroll);
      return;
    }

    setTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right,
    });
  }, [currentStepData, getTargetSelector]);

  // Update on step change
  useEffect(() => {
    const timer = setTimeout(updateTargetRect, 50);
    return () => clearTimeout(timer);
  }, [updateTargetRect]);

  // Track scroll and resize
  useEffect(() => {
    const handleUpdate = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateTargetRect);
    };

    const handleResize = () => {
      handleUpdate();
    };

    window.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateTargetRect]);

  // Observe DOM changes
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new MutationObserver(updateTargetRect);
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => observerRef.current?.disconnect();
  }, [updateTargetRect]);

  // Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTutorial?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [skipTutorial]);

  if (!currentStepData) return null;

  const padding = currentStepData.padding ?? SPOTLIGHT_PADDING;

  const holeX = targetRect ? targetRect.left - padding : 0;
  const holeY = targetRect ? targetRect.top - padding : 0;
  const holeW = targetRect ? targetRect.width + padding * 2 : 0;
  const holeH = targetRect ? targetRect.height + padding * 2 : 0;
  const hasHole = targetRect && holeW > 0 && holeH > 0;

  const isInteractive = currentStepData.type === 'interactive' && waitingForInteraction;
  const pointerEvents = isInteractive ? 'pointer-events-auto' : 'pointer-events-auto';
  const blurStyle = { backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' };

  return createPortal(
    <div className="fixed inset-0 z-[60] pointer-events-none" aria-hidden="true">
      {hasHole ? (
        <>
          {/* Top */}
          <div
            className={`absolute left-0 right-0 top-0 bg-black/10 ${pointerEvents}`}
            style={{ height: Math.max(0, holeY), ...blurStyle }}
          />
          {/* Bottom */}
          <div
            className={`absolute left-0 right-0 bottom-0 bg-black/10 ${pointerEvents}`}
            style={{ top: holeY + holeH, ...blurStyle }}
          />
          {/* Left */}
          <div
            className={`absolute left-0 bg-black/10 ${pointerEvents}`}
            style={{ top: holeY, height: holeH, width: Math.max(0, holeX), ...blurStyle }}
          />
          {/* Right */}
          <div
            className={`absolute right-0 bg-black/10 ${pointerEvents}`}
            style={{ top: holeY, height: holeH, left: holeX + holeW, ...blurStyle }}
          />
          {/* Block clicks on hole for passive mode */}
          {!isInteractive && (
            <div
              className="absolute pointer-events-auto"
              style={{ top: holeY, left: holeX, width: holeW, height: holeH }}
            />
          )}
        </>
      ) : (
        <div
          className="absolute inset-0 bg-black/10 pointer-events-auto"
          style={blurStyle}
        />
      )}

      {/* Spotlight ring - yellow frame */}
      {hasHole && (
        <div
          className="absolute border-3 border-yellow-400 rounded-xl pointer-events-none transition-all duration-300 ease-out"
          style={{ top: holeY, left: holeX, width: holeW, height: holeH }}
        />
      )}

      {/* Tooltip */}
      {targetRect && <TutorialTooltip targetRect={targetRect} />}
    </div>,
    document.body,
  );
};

export default TutorialOverlay;
